import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { LogOut, Menu, RefreshCw, Settings, ShieldCheck } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient, useIsFetching } from '@tanstack/react-query'
import { ThemeToggle } from './ThemeToggle'

interface HeaderProps {
  onMenuClick?: () => void
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, adminUser, signOut } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isFetching = useIsFetching()

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  const getInitials = (email: string) => {
    return email.slice(0, 2).toUpperCase()
  }

  const getRoleBadgeColor = (role: string | undefined) => {
    switch (role) {
      case 'super_admin':
        return 'bg-brand-fg/20 text-brand-fg'
      case 'admin':
        return 'bg-blue-500/20 text-blue-400'
      case 'investor':
        return 'bg-emerald-500/20 text-emerald-400'
      default:
        return 'bg-muted text-muted-foreground'
    }
  }

  return (
    <header className="sticky top-0 z-40 h-16 bg-card/80 backdrop-blur border-b border-border">
      <div className="flex h-full items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-3">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 -ml-2 rounded-md hover:bg-muted min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <Menu className="h-5 w-5" />
          </button>
          {/* Mobile branding */}
          <div className="flex items-center gap-2 lg:hidden">
            <img src="/dextoro-logo.png" alt="dextoro" className="hidden dark:block h-7" />
            <img src="/dextoro-logo-black.png" alt="dextoro" className="block dark:hidden h-7" />
          </div>
          <h1 className="text-lg font-semibold hidden lg:block">Dashboard</h1>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {adminUser?.mfa_enabled && (
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-brand-fg">
              <ShieldCheck className="h-4 w-4" />
              <span>2FA Active</span>
            </div>
          )}

          <ThemeToggle />

          <Button
            variant="ghost"
            size="icon"
            title="Refresh data"
            disabled={isFetching > 0}
            onClick={() => queryClient.invalidateQueries()}
          >
            <RefreshCw className={`h-[18px] w-[18px] ${isFetching > 0 ? 'animate-spin' : ''}`} />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-brand-fg/10 text-brand-fg">
                    {user?.email ? getInitials(user.email) : 'AD'}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user?.email}</p>
                  <div className="flex items-center gap-2 pt-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${getRoleBadgeColor(
                        adminUser?.role
                      )}`}
                    >
                      {adminUser?.role?.replace('_', ' ') || 'Unknown'}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-red-500">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
